import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Slash, Calendar, Printer, User, Briefcase, FileText, CreditCard } from "react-feather";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {
  useGetPettyCashRefillQuery,
  useApprovePettyCashRefillMutation,
  useRejectPettyCashRefillMutation,
  useCancelPettyCashRefillMutation,
  useIssuePettyCashRefillMutation,
  useGetPettyCashFundQuery,
} from "../../pettyCashApi";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import { PETTY_CASH_REFILL_STATUS_CLASS, PETTY_CASH_REFILL_STATUS_LABEL } from "../../pettyCashStatusMaps";
import { useCanApproveRefill, useCanIssueRefill, useCanSelfApprove } from "../../usePettyCashPermissions";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";
import RefillPrintModal from "../components/RefillPrintModal";
import RefillAttachment from "../components/RefillAttachment";

const money = (n) => (n == null ? "-" : Number(n).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// Dates on this screen read dd/MMM/yy (2026-09-22, by instruction); the audit trail keeps the time.
const dmy = (v) => (v ? dayjs(v).format("DD/MMM/YY") : "-");
const dmyTime = (v) => (v ? dayjs(v).format("DD/MMM/YY HH:mm") : "-");

const MAROON = "#6E1423";

/**
 * A refill request, and the one decision that settles it.
 *
 * The separate "Issue" step was removed on 2026-09-08 (Batch141): approving now posts the
 * credit to the fund itself, so approval IS the payment. The evidence that the old two-step
 * flow was not working is unambiguous - on live production 12 requests sat in SUBMITTED and
 * not one refill had ever been issued to a fund.
 *
 * Approve therefore moves money now, which is why it confirms first and says the amount.
 *
 * Layout (2026-09-22): the amount is the headline - a big figure in a maroon-railed panel -
 * with the fund, requester and date beside it. Suggested amount and minimum balance were
 * removed from the view by instruction (they distracted from the one figure being decided).
 * A supporting document (Batch195) can be attached while the request is awaiting approval.
 */
const RefillDetail = () => {
  const { recordGuid } = useParams();
  const { userId } = useClaims();
  const canApprove = useCanApproveRefill();
  const canSelfApprove = useCanSelfApprove();

  const { data: refill, isLoading, isError, error } = useGetPettyCashRefillQuery(recordGuid);
  const [approveRefill, { isLoading: isApproving }] = useApprovePettyCashRefillMutation();
  const [rejectRefill] = useRejectPettyCashRefillMutation();
  const [cancelRefill] = useCancelPettyCashRefillMutation();
  const canCreditToFund = useCanIssueRefill();
  const [creditRefill, { isLoading: isCrediting }] = useIssuePettyCashRefillMutation();
  // Only an approved-but-uncredited refill can be credited, and only into a fund that is still
  // active (the legacy Bank of Baroda requests sit on a closed fund - the server refuses them).
  const awaitingCredit = refill?.status === "APPROVED" && refill?.actualAmountIssued == null;
  // Reading a fund needs PettyCash.ViewBalance, which a crediting user may lack - so hide only
  // when the fund is KNOWN to be closed; the server refuses a closed fund either way.
  const { data: fund, isFetching: isFundFetching } = useGetPettyCashFundQuery(refill?.fundId, {
    skip: !awaitingCredit || !canCreditToFund,
  });

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  if (isLoading) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }
  if (isError || !refill) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.pettyCashRefills} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Refills
          </Link>
          <div className="alert alert-danger">Could not load this refill request ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  // By user id - names are display text and two people can share one.
  const isOwner = userId != null && refill.requestedByUserId === userId;
  const isSubmitted = refill.status === "SUBMITTED";
  const canApproveOrRejectNow = isSubmitted && (isOwner ? canSelfApprove : canApprove);
  const canCancelNow = isSubmitted && isOwner;
  // The requester attaches while the request is still open; anyone who can see it can view.
  const canManageAttachment = isSubmitted && isOwner;
  // Printable once approved (ISSUED = approved and credited, APPROVED = legacy record-only approval).
  const canPrint = refill.status === "ISSUED" || refill.status === "APPROVED";

  const handleApprove = async () => {
    // This adds real money to a real fund now, and there is no undo - a wrong credit has
    // to be taken back out with an adjustment. Worth one confirmation naming the figure.
    const confirmed = window.confirm(
      `Approve this refill and add ${money(refill.requestedAmount)} to ${refill.fundName}?\n\n` +
        "The fund balance is credited immediately. This cannot be undone."
    );
    if (!confirmed) return;
    try {
      await approveRefill({ recordGuid }).unwrap();
      toast.success(`Approved - ${money(refill.requestedAmount)} added to ${refill.fundName}`);
    } catch (e) {
      toast.error(e?.message || "Could not approve the refill request");
    }
  };

  const showCredit = awaitingCredit && canCreditToFund && !isFundFetching && fund?.isActive !== false;

  const handleCredit = async () => {
    const confirmed = window.confirm(
      `Credit ${money(refill.requestedAmount)} to ${refill.fundName} for ${refill.requestNumber}?\n\n` +
        "Only do this if the cash was really received and is not already counted in the fund " +
        "(for example by a later refill or an adjustment). The fund balance changes immediately and this cannot be undone."
    );
    if (!confirmed) return;
    try {
      await creditRefill({ recordGuid }).unwrap();
      toast.success(`${money(refill.requestedAmount)} credited to ${refill.fundName}`);
    } catch (e) {
      toast.error(e?.message || "Could not credit the refill to the fund");
    }
  };

  const handleReject = async (rejectionReason) => {
    try {
      await rejectRefill({ recordGuid, rejectionReason }).unwrap();
      toast.success("Refill request rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the refill request");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this refill request?")) return;
    try {
      await cancelRefill(recordGuid).unwrap();
      toast.success("Refill request cancelled");
    } catch (e) {
      toast.error(e?.message || "Could not cancel the refill request");
    }
  };

  const trail = [
    ["Requested", refill.requestedByName, refill.createdDate],
    ["Approved", refill.approvedByName, refill.approvedDate],
    // Approval and crediting are one act now, so naming the same person twice at the same
    // second would be noise. Only shown when they really do differ (old two-step rows).
    refill.issuedByName && refill.issuedBy !== refill.approvedBy ? ["Credited to fund", refill.issuedByName, refill.issuedDate] : null,
    ["Rejected", refill.rejectedByName, refill.rejectedDate],
    ["Cancelled", refill.cancelledByName, refill.cancelledDate],
  ].filter((s) => s && s[1] && s[2]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.pettyCashRefills} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Refills
              </Link>
              <h4>
                {refill.requestNumber}{" "}
                <StatusBadge status={refill.status} classMap={PETTY_CASH_REFILL_STATUS_CLASS} labelMap={PETTY_CASH_REFILL_STATUS_LABEL} />
              </h4>
              <h6>{refill.fundName} &middot; {refill.branchCode}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2 flex-wrap">
            {canPrint && (
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => setShowPrint(true)}>
                <Printer size={16} /> Print
              </button>
            )}
            {showCredit && (
              <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleCredit} disabled={isCrediting}>
                <CheckCircle size={16} /> Credit to Fund
              </button>
            )}
            {canApproveOrRejectNow && (
              <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleApprove} disabled={isApproving}>
                <CheckCircle size={16} /> Approve &amp; Credit Fund
              </button>
            )}
            {canApproveOrRejectNow && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                <XCircle size={16} /> Reject
              </button>
            )}
            {canCancelNow && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={handleCancel}>
                <Slash size={16} /> Cancel
              </button>
            )}
          </div>
        </div>

        {/* The headline: the figure being decided, big, on a maroon rail - brand colour
            as a border only, never a fill behind text. */}
        <div className="card mb-4" style={{ borderLeft: `5px solid ${MAROON}` }}>
          <div className="card-body d-flex flex-wrap align-items-center gap-4 py-3">
            <div>
              <div className="text-uppercase text-muted fw-semibold" style={{ fontSize: 11, letterSpacing: ".08em" }}>
                Amount requested
              </div>
              <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1, color: MAROON, fontVariantNumeric: "tabular-nums" }}>
                {money(refill.requestedAmount)}
              </div>
              {refill.actualAmountIssued != null && Number(refill.actualAmountIssued) !== Number(refill.requestedAmount) && (
                <div className="small text-muted">credited {money(refill.actualAmountIssued)}</div>
              )}
            </div>
            <div className="vr d-none d-md-block" style={{ height: 56 }} />
            <div className="d-flex flex-column gap-1" style={{ fontSize: 14 }}>
              <span className="d-inline-flex align-items-center">
                <Briefcase size={15} className="me-2 text-muted" />
                <span className="fw-semibold">{refill.fundName}</span>
                <span className="text-muted ms-2">({refill.fundCode} &middot; {refill.branchCode})</span>
              </span>
              <span className="d-inline-flex align-items-center">
                <User size={15} className="me-2 text-muted" />
                Requested by <ActorName action="Requested" name={refill.requestedByName} className="ms-1" />
              </span>
              <span className="d-inline-flex align-items-center">
                <Calendar size={15} className="me-2 text-muted" />
                <span className="fw-semibold">{dmy(refill.requestedDate)}</span>
                <span className="text-muted ms-2">&middot; fund balance at request {money(refill.currentBalanceAtRequest)}</span>
              </span>
              {/* Batch215 (2026-10-22): a refill raised from a voucher names it and its cheque. */}
              {refill.voucherRecordGuid && (
                <span className="d-inline-flex align-items-center">
                  <FileText size={15} className="me-2 text-muted" />
                  Voucher:{" "}
                  <Link to={paths.pettyCashVoucherDetail(refill.voucherRecordGuid)} className="fw-semibold ms-1">
                    {refill.voucherNumber || "View voucher"}
                  </Link>
                </span>
              )}
              {refill.chequeNumber && (
                <span className="d-inline-flex align-items-center">
                  <CreditCard size={15} className="me-2 text-muted" />
                  Cheque No.: <span className="fw-semibold ms-1">{refill.chequeNumber}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {showRejectForm && (
          <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />
        )}

        {refill.status === "ISSUED" && (
          <div className="alert alert-success">
            {money(refill.actualAmountIssued ?? refill.requestedAmount)} was added to {refill.fundName}
            {refill.issuedBy && refill.issuedBy !== refill.approvedBy
              ? ` by ${refill.issuedByName}`
              : refill.approvedByName && ` on approval by ${refill.approvedByName}`}
            {" "}on {dmy(refill.issuedDate || refill.approvedDate)}.
          </div>
        )}
        {refill.status === "REJECTED" && refill.rejectionReason && (
          <div className="alert alert-danger">Rejected by {refill.rejectedByName} on {dmy(refill.rejectedDate)}: {refill.rejectionReason}</div>
        )}
        {refill.status === "CANCELLED" && (
          <div className="alert alert-secondary">Cancelled by {refill.cancelledByName} on {dmy(refill.cancelledDate)}</div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row mb-3">
              <div className="col-md-6"><strong>Reason:</strong> {refill.reason || "-"}</div>
              {refill.paymentReference && <div className="col-md-3"><strong>Payment Reference:</strong> {refill.paymentReference}</div>}
              {refill.approvalComment && <div className="col-md-3"><strong>Approval Comment:</strong> {refill.approvalComment}</div>}
            </div>

            <div className="mb-3">
              <RefillAttachment
                recordGuid={refill.recordGuid}
                hasAttachment={refill.hasAttachment}
                fileName={refill.attachmentFileName}
                canManage={canManageAttachment}
              />
            </div>

            <ul className="text-muted small mb-0 list-unstyled">
              {trail.map(([step, who, when]) => (
                <li key={step}>&bull; {step} by <ActorName action={step} name={who} /> on {dmyTime(when)}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {canPrint && <RefillPrintModal show={showPrint} onClose={() => setShowPrint(false)} refill={refill} />}
    </div>
  );
};

export default RefillDetail;
